import React, {
  useCallback, useEffect, useMemo, useRef,
} from "react"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import {
  DioboxEvent, ModalName, TextMessage, WhatsAppTemplate,
} from "sharedTypes"
import SMSEditor from "components/SMSEditor/SMSEditor"
import TemplateEditor, { TemplateAttachments, TemplateButtons } from "components/TemplateEditor/TemplateEditor"
import TemplateUnavailable from "components/TemplateEditor/TemplateUnavailable"
import InlineWarning from "components/elements/InlineWarning"
import {
  Control, UseFormGetValues, UseFormWatch, useWatch,
} from "react-hook-form"
import useModal from "services/useModal"
import _ from "lodash"
import Button from "components/Buttons/Button"
import Icon from "components/elements/Icon"
import Popover from "components/dialogs/Popover"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import TextChannelIcon from "components/elements/TextChannelIcon"
import { getMissingBodyVariableLabels, hasQRCodeConflict } from "utilities/whatsappTemplates"
import Link from "components/elements/Link"
import { WHATSAPP_US_RESTRICTION_ARTICLE_LINK } from "utilities/externalLinks"
import SelectedRecipients from "./SelectedRecipients"
import OptionsRow from "./OptionsRow"
import { TEXT_MESSAGE_BODY_CHARACTER_LIMIT } from "../SelectedTextMessage"

type Props = {
  control: Control
  selectedTextMessage: TextMessage
  handleSave: (textMessage: TextMessage, options?: { silent?: boolean }) => void
  event: DioboxEvent
  watch: UseFormWatch<any>
  getValues: UseFormGetValues<any>
  showSavedLabel: boolean
}

const FormContent = ({
  control, selectedTextMessage, handleSave, event, watch, getValues, showSavedLabel,
}: Props) => {
  const showModal = useModal()

  const { channel } = selectedTextMessage

  const {
    template, isTemplateBased, templateUnavailable,
  } = useWhatsAppTemplate(selectedTextMessage?.whatsappContentSid)

  const temporalQRCodeConflict = hasQRCodeConflict(template, event.expiringQrCodes)

  const showTemplateEditor = channel === "whatsapp" && isTemplateBased

  const saveCurrentValues = useCallback(() => {
    handleSave(getValues())
  }, [handleSave, getValues])

  const handleSaveRef = useRef(handleSave)
  handleSaveRef.current = handleSave

  const debouncedSave = useMemo(
    () => _.debounce((formData: TextMessage) => handleSaveRef.current(formData), 2000),
    [],
  )

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave])

  useEffect(() => {
    if (showTemplateEditor) return undefined

    const subscription = watch((formData, { type }) => {
      if (type === "change") {
        debouncedSave(formData)
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, showTemplateEditor, debouncedSave])

  const hasSelectedRecipients = selectedTextMessage?.recipients.length > 0

  return (
    <Stack spacing={8} my={3} ml={1.5}>
      <Box>
        <Flex alignItems="center" gap={2} mb={1}>
          <TextChannelIcon channel={channel} size={6} />
          <Text fontWeight="semibold" fontSize="xl">
            {channel === "whatsapp" ? "WhatsApp Message" : "SMS Message"}
          </Text>
          {showSavedLabel && <Text color="gray.400" fontSize="sm" ml="auto">Saved</Text>}
          {channel === "whatsapp" && template?.category === "marketing" && (
            <Flex alignItems="center" gap={1} ml={!showSavedLabel ? "auto" : 0}>
              <Text color="red.500">
                Not sent to U.S. numbers
              </Text>
              <Popover
                placement="bottom"
                triggerEvent="hover"
                contentWidth="xs"
                trigger={(
                  <Icon icon="info" size={3.5} color="red.500" cursor="help" />
                )}
                body={(
                  <Box p={3}>
                    <Text>
                      This message uses a marketing template. Meta does not allow
                      WhatsApp marketing messages to be sent to phone numbers
                      registered in the United States. These recipients will be
                      excluded from delivery. To reach them, use the SMS Fallback
                      option to send this message via SMS instead.
                      {" "}
                      <Link href={WHATSAPP_US_RESTRICTION_ARTICLE_LINK} target="_blank">
                        Learn more
                      </Link>
                    </Text>
                  </Box>
                )}
              />
            </Flex>
          )}
        </Flex>
        {showTemplateEditor && templateUnavailable && <TemplateUnavailable />}
        {showTemplateEditor && !templateUnavailable && (
          <>
            <TemplateEditor
              control={control}
              contentSid={selectedTextMessage.whatsappContentSid!}
              event={event}
              textMessage={selectedTextMessage}
              onSave={saveCurrentValues}
            />
            <MissingBodyWarning control={control} template={template!} />
          </>
        )}
        {!showTemplateEditor && (
          <SMSEditor
            name="body"
            control={control}
            characterLimit={TEXT_MESSAGE_BODY_CHARACTER_LIMIT}
            event={event}
            onSave={saveCurrentValues}
          />
        )}
      </Box>
      <Stack spacing={4}>
        {showTemplateEditor && !templateUnavailable && (
          <>
            <TemplateAttachments
              control={control}
              template={template!}
              event={event}
              textMessage={selectedTextMessage}
              onSave={saveCurrentValues}
            />
            <TemplateButtons template={template!} />
          </>
        )}
        {channel === "whatsapp" && template?.messageType !== "opt_in" && (
          <OptionsRow event={event} />
        )}
        {template?.messageType !== "opt_in" && (
          <Flex alignItems="flex-start">
            <Text fontWeight="bold" w="25%" pt={1}>Recipients</Text>
            <Box flex={1}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => showModal(
                  ModalName.EditTextRecipients,
                  { onSave: handleSave, textMessage: selectedTextMessage },
                )}
              >
                Edit Recipients
              </Button>
              {hasSelectedRecipients && (
                <Box mt={2}>
                  <SelectedRecipients
                    recipients={selectedTextMessage?.recipients}
                    guestCount={selectedTextMessage?.guestCount}
                    event={event}
                  />
                  {channel === "whatsapp" && (
                    <WhatsAppRecipientBreakdown
                      optedInCount={selectedTextMessage.optedInRecipientCount}
                      unoptedCount={selectedTextMessage.unoptedRecipientCount}
                      autoOptInEnabled={event.autoOptInEnabled}
                      eventHasOptInText={event.defaultOptInTextId !== null}
                      isOptInTemplate={template?.messageType === "opt_in"}
                    />
                  )}
                </Box>
              )}
              {!hasSelectedRecipients && (
                <InlineWarning>Not selected</InlineWarning>
              )}
            </Box>
          </Flex>
        )}
        {temporalQRCodeConflict && (
          <Box
            borderWidth={1}
            borderColor="gray.300"
            borderRadius="md"
            p={4}
          >
            <Text color="red.500">
              Your event uses Temporal QR codes, which are not compatible with
              the standard QR codes in this template. Please select a template
              designed for Temporal QR codes.
            </Text>
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

// Scopes the re-render to this component only. The parent FormContent doesn't
// subscribe to whatsappContentVariables — useWatch here is what causes
// MissingBodyWarning (and only MissingBodyWarning) to update as pills get
// filled in.
const MissingBodyWarning = ({
  control, template,
}: { control: Control<any>; template: WhatsAppTemplate }) => {
  const watchedVariables = useWatch({
    control, name: "whatsappContentVariables",
  }) as Record<string, string> | undefined
  const labels = getMissingBodyVariableLabels(
    { whatsappContentVariables: watchedVariables },
    template,
  )

  if (labels.length === 0) return null

  const copy = labels.length === 1
    ? `Required field missing: ${labels[0]}`
    : `Required fields missing: ${labels.join(", ")}`

  return <InlineWarning>{copy}</InlineWarning>
}

type WhatsAppRecipientBreakdownProps = {
  optedInCount?: number
  unoptedCount?: number
  autoOptInEnabled: boolean
  eventHasOptInText: boolean
  isOptInTemplate: boolean
}

const WhatsAppRecipientBreakdown = ({
  optedInCount, unoptedCount, autoOptInEnabled, eventHasOptInText, isOptInTemplate,
}: WhatsAppRecipientBreakdownProps) => {
  if (optedInCount === undefined || unoptedCount === undefined) return null
  if (optedInCount === 0 && unoptedCount === 0) return null

  if (isOptInTemplate) {
    // Inverted breakdown: opt-in template targets unopted recipients; the BE
    // gate silently drops already-opted-in (no redundant prompt, no charge).
    return (
      <Box mt={1} pl={4} fontSize="sm">
        {unoptedCount > 0 && (
          <Text>
            {`• ${unoptedCount} not opted-in → receive the opt-in message`}
          </Text>
        )}
        {optedInCount > 0 && (
          <Text color="gray.600">
            {`• ${optedInCount} opted-in → already consented, won't receive this`}
          </Text>
        )}
      </Box>
    )
  }

  const autoOnAndConfigured = autoOptInEnabled && eventHasOptInText
  const notConfigured = autoOptInEnabled && !eventHasOptInText

  let unoptedText: string
  let unoptedColor: string | undefined
  if (autoOnAndConfigured) {
    unoptedText = "receive the opt-in message first"
    unoptedColor = undefined
  } else if (notConfigured) {
    unoptedText = "will not receive the message"
    unoptedColor = "orange.400"
  } else {
    unoptedText = "will not receive the message"
    unoptedColor = "red.500"
  }

  return (
    <Box mt={1} pl={4} fontSize="sm">
      {optedInCount > 0 && (
        <Text>
          {`• ${optedInCount} opted-in → receive this message`}
        </Text>
      )}
      {unoptedCount > 0 && (
        <Text color={unoptedColor}>
          {`• ${unoptedCount} not opted-in → ${unoptedText}`}
        </Text>
      )}
    </Box>
  )
}

export default FormContent
